import { useCallback, useEffect, useRef, useState } from 'react'

/** Load data once the user is known, with loading/error state and a reload.
 *  Stale responses from an earlier call never overwrite a newer one. */
export function useAsync<T>(load: () => Promise<T>, enabled: boolean) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)
  const loader = useRef(load)
  loader.current = load

  const reload = useCallback(async () => {
    const mine = ++seq.current
    setLoading(true)
    setError(null)
    try {
      const value = await loader.current()
      if (mine === seq.current) setData(value)
    } catch (err) {
      if (mine === seq.current) setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      if (mine === seq.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (enabled) void reload()
  }, [enabled, reload])

  return { data, error, loading, reload, setData }
}
